import TableWithExpandedRows from "reports/components/Reports/BespokeReports/Charts/TableWithExpandedRows";
import { useGeAndLtFromUrl } from "reports/components/Reports/reportUtils";
import styled from "styled-components/macro";
import { eosHeader, oddRowBackground } from "reports/constants/colors";
import { FONT_SIZE_MEDIUM } from "reports/constants/constants";
import LoadingCircle from "reports/components/simple/LoadingCircle";
import PnlBreakdownGetter from "reports/getters/pnlBreakdownGetter/PnlBreakdownGetter";
import {
  PnlBreakdown,
  PnlBreakdownFiltersEnum,
} from "reports/getters/pnlBreakdownGetter/pnlBreakdownTypes";
import { useState } from "react";
import { EOS_TOP_PADDING_PX } from "./eosConstants";
import { useTranslation } from "react-i18next";
import {
  getSummaryMetrics,
  sortByOrder,
  SummaryMetricInput,
  useSummaryMetrics,
} from "reports/utils/metricUtils";
import { SummaryMetricType } from "./SummaryMetricsRow";
import { SKU_METRIC_COLUMNS_PER_CUSTOMER } from "reports/constants/customers";
import { MetricEnum, TimeUnitsEnum } from "reports/utils/metricTypes";
import { useParams } from "react-router";
import { ReportParams } from "reports/types";

export default function SkuCountTable({
  skuSummaryMetrics = DEFAULT_SKU_METRICS,
  machemetryIds,
}: {
  skuSummaryMetrics?: SummaryMetricInput[];
  machemetryIds: string[];
}) {
  const { ge, lt } = useGeAndLtFromUrl();
  const { t } = useTranslation("EOSReport");
  const { ckey: tenant } = useParams() as ReportParams;
  const [pnlBreakdownsBySKU, setPnlBreakdownsBySKU] = useState(
    [] as PnlBreakdown[]
  );

  const customerDefinedMetrics = SKU_METRIC_COLUMNS_PER_CUSTOMER?.[tenant];

  const getCustomAndDefaultSummaryMetricsInputList = (
    pnlBreakdown: PnlBreakdown
  ) =>
    (customerDefinedMetrics
      ? [
          ...(customerDefinedMetrics?.metrics ?? []),
          ...(customerDefinedMetrics?.getCustomSummaryMetricsInputListWithPnlBreakdown?.(
            pnlBreakdown
          ) ?? []),
        ]
      : skuSummaryMetrics
    ).sort(sortByOrder);

  const pnlBreakdownsBySKUSorted = pnlBreakdownsBySKU.sort((a, b) =>
    customerDefinedMetrics?.customSortFn
      ? customerDefinedMetrics.customSortFn(a, b)
      : (b.oeePercent ?? 0) - (a.oeePercent ?? 0)
  );

  const summaryMetricsRow1 = useSummaryMetrics({
    metrics: getCustomAndDefaultSummaryMetricsInputList(pnlBreakdownsBySKU[0]),
    pnlBreakdown: pnlBreakdownsBySKU[0],
    useShortName: true,
  });
  const summaryMetricsFiltered = summaryMetricsRow1;

  const tableHeaders = summaryMetricsFiltered.map(({ name }) => name);

  const tableContent = getSkuMetricTableContent(
    pnlBreakdownsBySKUSorted,
    getCustomAndDefaultSummaryMetricsInputList,
    t
  );

  const numericColumns = getNumericColumns(summaryMetricsFiltered);

  return (
    <>
      <PnlBreakdownGetter
        range={{ ge, lt }}
        machemetryIds={machemetryIds}
        setPnlBreakdownData={setPnlBreakdownsBySKU}
        filter={PnlBreakdownFiltersEnum.sku}
        shouldFilterByMachemetryId={true}
        excludeNullFilterValue={true}
      />
      {machemetryIds &&
      machemetryIds.length > 0 &&
      pnlBreakdownsBySKU &&
      pnlBreakdownsBySKU.length > 0 ? (
        <SkuCountTableStyles>
          <div className="header">Performance Table</div>
          <TableWithExpandedRows
            tableHeaders={tableHeaders}
            tableContent={tableContent}
            numericColumns={numericColumns}
          />
        </SkuCountTableStyles>
      ) : (
        <LoadingCircle />
      )}
    </>
  );
}

const SkuCountTableStyles = styled.div`
  margin: 0 auto;
  width: 80vw;
  padding-bottom: 1em;

  .header {
    padding: ${EOS_TOP_PADDING_PX}px 0 5px;
    color: ${eosHeader};
    text-align: center;
    font-size: ${FONT_SIZE_MEDIUM}px;
    font-family: "GothamBold";
  }

  table {
    margin-top: 10px !important;
    tbody {
      tr:nth-child(odd) {
        background-color: ${oddRowBackground};
      }
    }
  }

  @media print {
    width: 95vw;
    @page {
      size: landscape;
    }
  }
`;

/**
 * From SummaryMetrics, get indexes of columns that are numerical
 *
 * @param {SummaryMetricType[]} summarMetrics
 * @return {*}
 */
function getNumericColumns(summarMetrics: SummaryMetricType[]) {
  return summarMetrics
    .map(({ numericValue }, index) => {
      return numericValue === null || isNaN(numericValue) ? -1 : index;
    })
    .filter(i => i !== -1);
}

/**
 * Get tableContent array for SkuMetricReport
 *
 * @param {PnlBreakdown[]} pnlBreakdowns
 * @param {(pnlBreakdowns) => SummaryMetricInput[]} getCustomAndDefaultSummaryMetricsInputList
 * @param {*} t
 * @return {*}
 */
function getSkuMetricTableContent(
  pnlBreakdowns: PnlBreakdown[],
  getCustomAndDefaultSummaryMetricsInputList: (
    pnlBreakdowns
  ) => SummaryMetricInput[],
  t
) {
  return pnlBreakdowns.map(pnlBreakdown => {
    return {
      data: getSummaryMetrics({
        metrics: getCustomAndDefaultSummaryMetricsInputList(pnlBreakdown),
        pnlBreakdown,
        t,
        targets: {},
      }).map(({ numericValue, stringFormattedValue, unit }) => {
        const value = `${numericValue ?? stringFormattedValue ?? "--"}`;
        return value === "--" ? "--" : `${value}${unit ?? ""}`;
      }),
    };
  });
}

export const DEFAULT_SKU_METRICS = [
  { name: MetricEnum.SKU },
  { name: MetricEnum.SKU_CODE },
  { name: MetricEnum.PROCESS_NAME },
  { name: MetricEnum.COUNT },
  { name: MetricEnum.TARGET_RATE },
  { name: MetricEnum.OPERATION_TIME, unit: TimeUnitsEnum.hoursAndMinutes },
  { name: MetricEnum.OEE },
];
